import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Checkbox, BlockStack, Text, Banner } from '@shopify/polaris';
import { getRequest } from '../functions';

const PART_DOCUMENT_TYPE_ID = 25;

/*
  Asks whether the documents of a part (production drawings, ...) should be added to the inquiry /
  purchase order the part was just added to. Only prompts when the part has documents that were not
  offered before and are not attached yet. On a document that is not saved yet the chosen files are
  kept and copied right after the first save (the page stays mounted when /new becomes /:id).

  resourceName: 'inquiries' | 'purchases' (POST /api/{resourceName}/:id/part-documents)
*/
export function usePartDocumentsPrompt({ resourceName, resourceId, docTypeId, label, onCopied }) {
  const [groups, setGroups] = useState([]); // [{ productId, name, documents: [{ DocumentID, Filename }] }]
  const [selected, setSelected] = useState(new Set());
  const [pending, setPending] = useState([]); // document ids chosen while the resource is still new
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null); // { tone, message }
  const offered = useRef(new Set()); // document ids already shown once on this page
  const checking = useRef(new Set()); // product ids being looked up (picker + blur fire for the same part)
  const isNew = !resourceId || resourceId === 'new';
  // react-table keeps the cell renderers of its first render, so promptForProducts must not close over the id
  const resourceIdRef = useRef(resourceId);
  resourceIdRef.current = resourceId;

  const copyDocuments = useCallback(
    async (targetId, documentIds) => {
      setCopying(true);
      try {
        const response = await fetch(`/api/${resourceName}/${targetId}/part-documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documentIds })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok && !data.copied) throw new Error(data.msg || `HTTP ${response.status}`);
        const failed = data.failed || [];
        const copied = data.copied || [];
        if (failed.length > 0) {
          setResult({ tone: 'warning', message: `${copied.length} document(s) added, ${failed.length} failed: ${failed.map((f) => f.filename).join(', ')}` });
        } else if (copied.length > 0) {
          setResult({ tone: 'success', message: `${copied.length} part document(s) added to this ${label}.` });
        }
        if (copied.length > 0 && onCopied) onCopied();
      } catch (err) {
        console.log('Error copying part documents', err);
        setResult({ tone: 'critical', message: `Part documents could not be added: ${err.message}` });
      } finally {
        setCopying(false);
      }
    },
    [resourceName, label, onCopied]
  );

  // first save of a new document: copy what was chosen before
  useEffect(() => {
    if (isNew || pending.length === 0) return;
    const documentIds = pending;
    setPending([]);
    copyDocuments(resourceId, documentIds);
  }, [isNew, resourceId, pending, copyDocuments]);

  const promptForProducts = useCallback(
    async (productIds) => {
      const ids = [...new Set((productIds || []).map((value) => parseInt(value)).filter((value) => value > 0 && !checking.current.has(value)))];
      if (ids.length === 0) return;
      ids.forEach((productId) => checking.current.add(productId));
      try {
        const currentId = resourceIdRef.current;
        const attached = !currentId || currentId === 'new' ? [] : await getRequest(`/api/documents/id/typeid?id=${currentId}&typeID=${docTypeId}`);
        const attachedNames = new Set((Array.isArray(attached) ? attached : []).map((doc) => String(doc.Filename || '').toLowerCase()));

        const found = await Promise.all(
          ids.map(async (productId) => {
            const documents = await getRequest(`/api/documents/id/typeid?id=${productId}&typeID=${PART_DOCUMENT_TYPE_ID}`);
            const fresh = (Array.isArray(documents) ? documents : []).filter((doc) => !offered.current.has(doc.DocumentID) && !attachedNames.has(String(doc.Filename || '').toLowerCase()));
            if (fresh.length === 0) return null;
            const product = await getRequest(`/api/products/${productId}`);
            return { productId, name: product?.ProductNameNL || product?.name || '', documents: fresh };
          })
        );
        const newGroups = found.filter(Boolean);
        if (newGroups.length === 0) return;

        newGroups.forEach((group) => group.documents.forEach((doc) => offered.current.add(doc.DocumentID)));
        setGroups((old) => [...old, ...newGroups]);
        setSelected((old) => new Set([...old, ...newGroups.flatMap((group) => group.documents.map((doc) => doc.DocumentID))]));
      } finally {
        ids.forEach((productId) => checking.current.delete(productId));
      }
    },
    [docTypeId]
  );

  const close = () => {
    setGroups([]);
    setSelected(new Set());
  };

  const confirm = async () => {
    const documentIds = [...selected];
    close();
    if (documentIds.length === 0) return;
    if (isNew) {
      setPending((old) => [...new Set([...old, ...documentIds])]);
      return;
    }
    await copyDocuments(resourceId, documentIds);
  };

  const toggle = (documentId) =>
    setSelected((old) => {
      const next = new Set(old);
      next.has(documentId) ? next.delete(documentId) : next.add(documentId);
      return next;
    });

  const modal = (
    <Modal
      open={groups.length > 0}
      onClose={close}
      title={`Add part documents to this ${label}?`}
      primaryAction={{ content: isNew ? `Add after saving (${selected.size})` : `Add ${selected.size} document(s)`, onAction: confirm, disabled: selected.size === 0 }}
      secondaryActions={[{ content: 'No thanks', onAction: close }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p" tone="subdued">
            {groups.length === 1 ? 'This part has' : 'These parts have'} documents attached (drawings, data sheets, ...). Copy them into the {label} so they can be sent along.
          </Text>
          {groups.map((group) => (
            <BlockStack gap="100" key={group.productId}>
              <Text as="h3" variant="headingSm">
                #{group.productId} {group.name}
              </Text>
              {group.documents.map((doc) => (
                <Checkbox key={doc.DocumentID} label={doc.Filename} checked={selected.has(doc.DocumentID)} onChange={() => toggle(doc.DocumentID)} />
              ))}
            </BlockStack>
          ))}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );

  const banner =
    copying || pending.length > 0 || result ? (
      <Banner tone={copying || pending.length > 0 ? 'info' : result.tone} onDismiss={copying || pending.length > 0 ? undefined : () => setResult(null)}>
        {copying ? 'Adding part documents...' : pending.length > 0 ? `${pending.length} part document(s) will be added when this ${label} is saved.` : result.message}
      </Banner>
    ) : null;

  return { promptForProducts, modal, banner };
}
